import { MapViewState, PaneState } from '@/models/externalReports/standard/standardMap';
import { computed, onMounted, onUnmounted, reactive, Ref, ref, UnwrapRef } from '@vue/composition-api';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  PANE_SIDE_MAX_WIDTH_RATE,
  PANE_SIDE_MIN_WIDTH_RATE,
  VERTICAL_PANE_PADDING_NUMBER,
} from '@/components/ExternalReports/Standard/consts';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import { useStore } from '@/hooks/useStore';
import { Settings as UserSettings } from '@/models/apis/user/userResponse';
import type { StandardCarExt } from '@/models/externalReports/standard/standardCar';
import type { StandardMtxExt } from '@/models/externalReports/standard/standardMtx';
import type { StandardReportExt, StandardReportPhotoExt } from '@/models/externalReports/standard/standardReport';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import { dtFormat } from '@/lib/dateHelper';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/standard';

interface RedrawCarLayerOptions {
  fitToExtent?: boolean;
  setSelectedCarToCenter?: boolean;
}
export interface UseExtremeMapResult {
  extremeMapRef: Ref;
  mapViewState: UnwrapRef<MapViewState>;
  loadExtremeMapEssentials: () => Promise<void>;
  forceResizeExtremeMap: () => void;
  redrawCarLayer: (options: RedrawCarLayerOptions) => void;
  showStandardReportLayer: (selectedReport: StandardReportExt, fitToExtent: boolean) => void;
  removeStandardReportLayer: () => void;
  showStandardCarPopup: (car: StandardCarExt) => void;
  showStandardReportPhotoPopup: (photo: StandardReportPhotoExt) => void;
  showStandardMtxPopup: (mtx: StandardMtxExt) => void;
  hidePopup: () => void;
}

export function useExtremeMap(paneState: PaneState): UnwrapRef<UseExtremeMapResult> {
  const store = useStore();
  const userState = store.state.user;
  const userSettings = computed<UserSettings>(() => {
    return userState.settings;
  });
  const standardUserSettings = computed<ExternalReportSettings | null>(() => {
    const standardUserSettings = window.master.externalReportSettings?.find(
      setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
    );
    if (!standardUserSettings) return null;

    return standardUserSettings;
  });

  const extremeMapRef = ref<InstanceType<typeof ExtremeMap>>();

  const mapViewState = reactive<MapViewState>({
    extremeMapEssentials: null,
    onResizeFunc: () => {},
  });

  const loadExtremeMapEssentials = async(): Promise<void> => {
    await waitForUserAndMasters();
    mapViewState.extremeMapEssentials = {
      userSettings: userSettings.value,
      kpMap: window.master.kpMap,
      externalReportSettings: standardUserSettings.value,
    };
  };

  const resizeMap = (mapHeight: number) => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.setMapHeight(mapHeight);
    extremeMapRef.value.triggerResize();
  };

  const initResizeFunc = () => {
    mapViewState.onResizeFunc = () => {
      const headerH = 57;
      const searchBarH = 96;
      const h = window.innerHeight - headerH - searchBarH;
      const w = window.innerWidth - VERTICAL_PANE_PADDING_NUMBER;
      const paneStyle = paneState.paneStyle;
      paneStyle.paneSideMinWidth = Math.min(Math.floor(w * PANE_SIDE_MIN_WIDTH_RATE), PANE_SIDE_HARD_MAX_WIDTH_PX);
      paneStyle.paneSideMaxWidth = Math.min(Math.floor(w * PANE_SIDE_MAX_WIDTH_RATE), PANE_SIDE_HARD_MAX_WIDTH_PX);
      paneStyle.listMinHeight = Math.floor(h * (paneState.paneStyleLimits?.listMinHeightRate ?? 1.0));
      paneStyle.listMaxHeight = Math.floor(h * (paneState.paneStyleLimits?.listMaxHeightRate ?? 1.0));
      paneState.paneStyle = paneStyle;
      resizeMap(h);
    };
    mapViewState.onResizeFunc();
    window.addEventListener('resize', mapViewState.onResizeFunc);
  };

  const forceResizeExtremeMap = (): void => {
    mapViewState.onResizeFunc();
  };

  const redrawCarLayer = ({ fitToExtent, setSelectedCarToCenter }: RedrawCarLayerOptions = {}) => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.redrawExternalCarLayer({
      hideCarIcons: !paneState.showCarIcons,
      photoTypeVisibility: paneState.photoTypeVisibility,
      fitToExtent: !!fitToExtent,
      setSelectedCarToCenter,
      externalReportUserSettings: standardUserSettings.value,
      showDirectionArrow: false,
    });
  };

  const getStandardCarPopupContentInnerHtml = (car: StandardCarExt): string => {
    const pcLabels = standardUserSettings.value?.pc;
    let html = `<p class="title">【${car.carName}】</p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.field_label?.han_name ?? '作業班'}:</span>
        <span class="val ml2">${car.hanName}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.field_label?.status ?? '状態'}:</span>
        <span class="val ml2">${car.statusDisp}</span>
      </p>`;
    if (!car.isMoving || !car.reportExt) {
      return html;
    }

    html += `
      <p class="info-row-wrap">
        <span >${pcLabels?.field_label?.work_content ?? '作業内容'}: ${car.reportExt.contentsDisp}</span>
      </p>`;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.field_label?.route ?? '作業路線'}: ${car.reportExt.roadNames}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.departure_ts ?? '基地出発時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.baseDepartureTs, 'HH:MM')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.start_work_ts ?? '作業開始時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.operationStartTs, 'HH:MM')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.end_work_ts ?? '作業終了時間'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.operationEndTs, 'HH:MM')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.arrival_ts ?? '基地到着時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.baseArrivalTs, 'HH:MM')}</span>
      </p>`;
    return html;
  };

  const getStandardReportInfoContentInnerHtml = (info: StandardReportPhotoExt | StandardMtxExt): string => {
    const pcLabels = standardUserSettings.value?.pc;
    let html = `
      <p class="info-row">
        <span class="val">${pcLabels?.field_label?.han_name ?? '作業班'}:</span>
        <span class="val ml2">${info.hanName}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.field_label?.work_time ?? '作業日時'}:</span>
        <span class="val ml2">${dtFormat(info.ts)}</span>
      </p>`;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.field_label?.work_content ?? '作業内容'}: ${info.reportContents}</span>
      </p>`;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.field_label?.route ?? '作業路線'}: ${info.reportRoadNames}</span>
      </p>`;
    return html;
  };

  const getStandardReportPhotoPopupContentInnerHtml = (photo: StandardReportPhotoExt): string => {
    const photoTypeDisp = photo.photoType ?? '';
    let html =
      `<p class="title">【${photoTypeDisp}写真${photo.dispOrder}】</p>`;
    html += getStandardReportInfoContentInnerHtml(photo);
    html += `
      <div class="image-area" id="external-map-photo${photo.id}">
        <div class="image-box">
          <div class="image-wrap">
            <img src="${photo.savedImage}" class="image" />
          </div>
        </div>
      </div>`;
    return html;
  };

  const showStandardReportLayer = (selectedReport: StandardReportExt, fitToExtent: boolean): void => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.showExternalReportLayer(selectedReport, {
      photoTypeVisibility: paneState.photoTypeVisibility,
      fitToExtent: fitToExtent,
      externalReportUserSettings: standardUserSettings.value,
      showTrack: true,
    });
  };

  const removeStandardReportLayer = (): void => {
    if (!extremeMapRef.value) return;
    extremeMapRef.value.removeExternalReportLayer();
  };

  const showStandardCarPopup = (car: StandardCarExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getStandardCarPopupContentInnerHtml(car);
    extremeMapRef.value.showExternalCarPopup(car, innerHTML);
  };

  const showStandardReportPhotoPopup = (photo: StandardReportPhotoExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getStandardReportPhotoPopupContentInnerHtml(photo);
    extremeMapRef.value.showExternalReportPhotoPopup(photo, innerHTML);
  };

  const showStandardMtxPopup = (mtx: StandardMtxExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getStandardReportInfoContentInnerHtml(mtx);
    extremeMapRef.value.showExternalMtxPopup(mtx, innerHTML);
  };

  const hidePopup = (): void => {
    if (!extremeMapRef.value) return;
    extremeMapRef.value.hidePopup();
  };

  onMounted(async() => {
    initResizeFunc();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', mapViewState.onResizeFunc);
  });

  return {
    extremeMapRef,
    mapViewState,
    loadExtremeMapEssentials,
    forceResizeExtremeMap,
    redrawCarLayer,
    showStandardReportLayer,
    removeStandardReportLayer,
    showStandardCarPopup,
    showStandardReportPhotoPopup,
    showStandardMtxPopup,
    hidePopup,
  };
}
